import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CreateSimulationModal, Spinner } from '../../components';
import { AppRoutes, MAX_FORM_WIDTH, PUBLISH_PARAM } from '../../constants';
import {
  useAppDispatch,
  useAppSelector,
  useFeatureFlag,
  useGetUserPermissions,
  useWarnBeforeUnload,
} from '../../hooks';
import {
  clearProspectForm,
  ProspectFooterFields,
  setProspectFooterFields,
  setProspectForm,
} from '../../redux/reducers';
import { useGetOrganizationSettingsQuery, useGetPracticeProspectByIdMutation } from '../../services';
import { LD_FeatureFlags, Permissions, PracticeProspectDetails, ProspectPageTabs, ProspectType } from '../../types';
import ProspectPageFooter from './ProspectPageFooter';
import ProspectPageHeader from './ProspectPageHeader';
import ProspectPagePanel from './ProspectPagePanel';
import {
  AccountTab,
  ConfigurationTab,
  LeadTab,
  PersonalTab,
  PromptTab,
  ScenarioPanel,
  ScenarioTab,
} from './ProspectPageTabs';

const DEFAULT_ACTIVE_TAB = ProspectPageTabs.PERSONAL;

const ProspectPage = () => {
  const navigate = useNavigate();
  const { personaId } = useParams();
  const [searchParams] = useSearchParams();
  const isPublishing = searchParams.get(PUBLISH_PARAM) === 'true';

  const dispatch = useAppDispatch();
  const { orgId, category, type } = useAppSelector((state) => state.prospect);

  const user = useAppSelector((state) => state.auth.user);
  const permissions = user?.permissions || [];
  const canManageProspect = permissions.includes(Permissions.MANAGE_PROSPECT);

  const knowledgeHubFF = useFeatureFlag(LD_FeatureFlags.RELEASE_KNOWLEDGE_HUB);
  const { canUserEditLibrary, getCanUserEditSimInSelfService } = useGetUserPermissions();

  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  // The original prospect data that prefills the form when viewing an existing prospect.
  // This is used to reset the form to its initial state when discarding changes.
  // We omit the footer fields because they are edited on-the-fly.
  const [prospect, setProspect] = useState<Omit<PracticeProspectDetails, ProspectFooterFields>>();
  const [visitedTabs, setVisitedTabs] = useState(new Set([DEFAULT_ACTIVE_TAB]));

  const [getPracticeProspectById, { isLoading: isLoadingProspect }] = useGetPracticeProspectByIdMutation();

  const { data: orgConfigs } = useGetOrganizationSettingsQuery({
    orgId,
  });
  const { defaultScorecardTemplateId, defaultCoachingTemplateId } = orgConfigs || {};

  // Warn the user if they try to leave the page without saving.
  useWarnBeforeUnload();

  // Sets the form state to the provided prospect.
  const setForm = useCallback(
    (prospectToSet?: Omit<PracticeProspectDetails, ProspectFooterFields>) => {
      if (!prospectToSet) return;
      // If the prospect is being published, set isSubscribed to true,
      // and set the default scorecard and coaching template IDs.
      dispatch(
        setProspectForm({
          prospect: {
            ...prospectToSet,
            isSubscribed: isPublishing ? true : prospectToSet.isSubscribed,
            scorecardTemplateId:
              isPublishing && defaultScorecardTemplateId
                ? defaultScorecardTemplateId
                : prospectToSet.scorecardTemplateId,
            coachingTemplateId:
              isPublishing && defaultCoachingTemplateId ? defaultCoachingTemplateId : prospectToSet.coachingTemplateId,
          },
          canManageProspect,
        })
      );
    },
    [dispatch, canManageProspect, isPublishing, defaultScorecardTemplateId, defaultCoachingTemplateId]
  );

  // Fetch prospect data and populate form state when personaId is provided.
  useEffect(() => {
    if (!personaId) return;

    const fetchProspect = async () => {
      const { data: newProspect } = await getPracticeProspectById(personaId);
      if (newProspect) {
        // If the user does not have permission to edit the prospect, redirect to the simulations page.
        const canUserEditSimInSelfService = getCanUserEditSimInSelfService(
          newProspect.category,
          newProspect.isDraft,
          newProspect.userId
        );
        if (!canUserEditSimInSelfService || (isPublishing && !canUserEditLibrary)) {
          navigate(`${AppRoutes.SIMULATIONS}`);
        }

        setProspect(newProspect);
        const { isFavorited, notes, tags, ...rest } = newProspect;
        setForm(rest);
        dispatch(setProspectFooterFields({ isFavorited, notes, tags }));
        // If the prospect is being published, open the Configuration tab directly.
        if (isPublishing) {
          setActiveTab(ProspectPageTabs.CONFIGURATION);
        }
        // Mark all tabs as visited since we are viewing an existing prospect.
        setVisitedTabs(new Set(Object.values(ProspectPageTabs)));
      }
    };

    fetchProspect();
  }, [
    dispatch,
    canManageProspect,
    canUserEditLibrary,
    isPublishing,
    personaId,
    getCanUserEditSimInSelfService,
    getPracticeProspectById,
    setProspect,
    navigate,
    setForm,
  ]);

  // Resets the prospect form when the page is unmounted.
  useEffect(() => {
    return () => {
      dispatch(clearProspectForm());
    };
  }, [dispatch]);

  const renderTabContent = () => {
    switch (activeTab) {
      case ProspectPageTabs.PERSONAL:
        return { content: <PersonalTab /> };
      case ProspectPageTabs.LEAD:
        return { content: <LeadTab /> };
      case ProspectPageTabs.ACCOUNT:
        return { content: <AccountTab /> };
      case ProspectPageTabs.SCENARIO:
        return {
          content: <ScenarioTab />,
          panel: knowledgeHubFF && type !== ProspectType.CUSTOM ? <ScenarioPanel /> : null,
        };
      case ProspectPageTabs.CONFIGURATION:
        return { content: <ConfigurationTab /> };
      case ProspectPageTabs.PROMPT:
      default:
        return { content: <PromptTab /> };
    }
  };

  const { content, panel } = renderTabContent();

  if (isLoadingProspect && !prospect) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-base-100 flex h-full flex-col">
      <ProspectPageHeader
        activeTab={activeTab}
        visitedTabs={visitedTabs}
        setActiveTab={setActiveTab}
        setVisitedTabs={setVisitedTabs}
      />
      <div className="flex min-h-0 flex-1">
        <div className="flex w-full overflow-y-auto px-8">
          <div className="mx-auto h-fit py-4" style={{ width: MAX_FORM_WIDTH }}>
            {content}
          </div>
        </div>
        {panel && (
          <div className="flex py-4">
            <ProspectPagePanel>{panel}</ProspectPagePanel>
          </div>
        )}
      </div>
      <ProspectPageFooter
        activeTab={activeTab}
        visitedTabs={visitedTabs}
        resetForm={() => setForm(prospect)}
        setActiveTab={setActiveTab}
        setVisitedTabs={setVisitedTabs}
      />
      {/* Show the prospect designer modal if the designer modal fields are not set,
      meaning the user has just started creating a prospect. */}
      {(!orgId || !category || !type) && <CreateSimulationModal setVisitedTabs={setVisitedTabs} />}
    </div>
  );
};

export default ProspectPage;
